/**
 * Subscribes to the live monitor WebSocket for a given mountpoint and exposes
 * a rolling buffer of classified frames for a console-style UI.
 *
 * Backpressure strategy: render at most ~10 fps using requestAnimationFrame,
 * regardless of inbound frame rate. Frames are appended to a ring of 500.
 * Reconnects automatically with exponential backoff (max 10s) on abnormal
 * closures.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { TOKEN_STORAGE_KEY, wsBase } from "@/lib/api";

export type StreamRole = "publisher" | "consumer";
export type StreamDirection = "upstream" | "downstream";
export type FrameKind = "RTCM3" | "NMEA" | "RAW";

export interface StreamFrame {
  ts: number; // epoch seconds, fractional
  mountpoint: string;
  session_id: string;
  role: StreamRole;
  direction: StreamDirection;
  kind: FrameKind;
  subtype: string | null;
  length: number;
  hex: string;
  ascii: string;
}

export type StreamStatus = "idle" | "connecting" | "open" | "closed" | "error";

interface UseMountpointStreamOptions {
  enabled: boolean;
  mountpointId: string;
  role?: StreamRole;
  sessionId?: string;
  direction?: StreamDirection;
  bufferSize?: number;
}

const DEFAULT_BUFFER = 500;
const RENDER_THROTTLE_MS = 100;
const MAX_BACKOFF_MS = 10_000;

export interface UseMountpointStreamResult {
  frames: StreamFrame[];
  status: StreamStatus;
  paused: boolean;
  setPaused: (p: boolean) => void;
  clear: () => void;
}

export function useMountpointStream(
  options: UseMountpointStreamOptions,
): UseMountpointStreamResult {
  const { enabled, mountpointId, role, sessionId, direction } = options;
  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER;

  const bufferRef = useRef<StreamFrame[]>([]);
  const dirtyRef = useRef(false);
  const lastFlushRef = useRef(0);
  const pausedRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  const [frames, setFrames] = useState<StreamFrame[]>([]);
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [paused, setPausedState] = useState(false);

  const setPaused = useCallback((p: boolean) => {
    pausedRef.current = p;
    setPausedState(p);
  }, []);

  const clear = useCallback(() => {
    bufferRef.current = [];
    setFrames([]);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (!dirtyRef.current) return;
    const now = performance.now();
    if (now - lastFlushRef.current < RENDER_THROTTLE_MS) return;
    lastFlushRef.current = now;
    dirtyRef.current = false;
    // Copy the ref slice so React sees a new array reference.
    setFrames(bufferRef.current.slice());
  }, []);

  // Drive scheduleFlush off a rAF loop while WS is open.
  useEffect(() => {
    if (status !== "open") return;
    let rafId = 0;
    const tick = () => {
      scheduleFlush();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [status, scheduleFlush]);

  useEffect(() => {
    if (!enabled || !mountpointId) {
      return;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const qs = new URLSearchParams({ token });
      if (role) qs.set("role", role);
      if (sessionId) qs.set("session_id", sessionId);
      if (direction) qs.set("direction", direction);
      const url = `${wsBase()}/api/mountpoints/${mountpointId}/stream?${qs.toString()}`;

      setStatus("connecting");
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        reconnectAttemptsRef.current = 0;
        setStatus("open");
      };

      ws.onmessage = (event) => {
        if (pausedRef.current) return;
        if (typeof event.data !== "string") return;
        let frame: StreamFrame;
        try {
          frame = JSON.parse(event.data) as StreamFrame;
        } catch {
          return;
        }
        const buf = bufferRef.current;
        buf.push(frame);
        if (buf.length > bufferSize) {
          buf.splice(0, buf.length - bufferSize);
        }
        dirtyRef.current = true;
      };

      ws.onerror = () => {
        setStatus("error");
      };

      ws.onclose = (event) => {
        if (cancelled) return;
        setStatus("closed");
        if (event.code === 1000) return; // normal closure — don't reconnect
        // Exponential backoff: 500ms, 1s, 2s, 4s, 8s, 10s (max).
        const attempt = reconnectAttemptsRef.current;
        reconnectAttemptsRef.current = attempt + 1;
        const delay = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try {
          ws.close(1000, "unmount");
        } catch {
          /* noop */
        }
      }
    };
  }, [enabled, mountpointId, role, sessionId, direction, bufferSize]);

  // Reset buffer whenever filters change so we don't show frames from previous filter.
  useEffect(() => {
    bufferRef.current = [];
    setFrames([]);
  }, [mountpointId, role, sessionId, direction]);

  return { frames, status, paused, setPaused, clear };
}
